import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Grid,
  Card,
  CardMedia,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  Tab,
  Tabs,
  Rating,
  ListItemIcon,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Store as StoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  Description as DescriptionIcon,
  LocationOn,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Update as UpdateIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { providersApi } from '../services/api';
import SeededProviderPanel from './SeededProviderPanel';
import ProviderAdminEditPanel from './ProviderAdminEditPanel';
import ProviderSubscriptionCard from './ProviderSubscriptionCard';
import ProviderStatusChips from './ProviderStatusChips';
import AdminReviewsTab from './AdminReviewsTab';
import StoreCommissionSection from './StoreCommissionSection';
import { providerImageUrl } from '../services/imageUtils';

const ProviderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [tab, setTab] = useState('overview');
  const [busyVisibility, setBusyVisibility] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);

  const fetchProvider = async () => {
    const data = await providersApi.getProviderById(id);
    setProvider(data);
  };

  const fetchOrders = async (pg = ordersPage, size = ordersRowsPerPage) => {
    try {
      setOrdersLoading(true);
      const data = await providersApi.getProviderOrders(id, pg, size);
      setOrders(data.content || []);
      setOrdersTotal(data.totalElements || 0);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    const init = async () => {
      try {
        setLoading(true);
        await fetchProvider();
        setError('');
      } catch (err) {
        setError('Failed to load provider details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  useEffect(() => {
    if (id) fetchOrders(ordersPage, ordersRowsPerPage);
  }, [id, ordersPage, ordersRowsPerPage]);

  // `visible !== false`, matching ProviderEligibility and providerStatus.js: a legacy NULL is
  // visible, not hidden.
  const handleToggleVisible = async (e) => {
    const next = e.target.checked;
    setBusyVisibility(true);
    try {
      // adminUpdateProvider DOES return ProviderAdminDto (AdminProviderController:53), so its
      // response is safe to assign directly -- unlike the pro-plan endpoint below.
      const updated = await providersApi.adminUpdateProvider(provider.id, { visible: next });
      setProvider(updated);
      showSuccess(next ? 'Provider is now visible' : 'Provider is now hidden');
    } catch (err) {
      showError(`Update failed: ${err.backendMessage || err.message}`);
    } finally {
      setBusyVisibility(false);
    }
  };

  const showSuccess = (msg) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); };
  const showError = (msg) => { setError(msg); setTimeout(() => setError(''), 5000); };

  const handleApproveProvider = async () => {
    try {
      setLoading(true);
      await providersApi.approveProvider(id);
      await fetchProvider();
      showSuccess('Provider has been approved.');
    } catch (err) {
      showError(`Failed to approve provider: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineProvider = async () => {
    try {
      setLoading(true);
      await providersApi.declineProvider(id);
      await fetchProvider();
      showSuccess('Provider has been declined.');
    } catch (err) {
      showError(`Failed to decline provider: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    try {
      setLoading(true);
      await providersApi.suspendProvider(id);
      await fetchProvider();
      showSuccess('Provider has been suspended.');
    } catch (err) {
      showError(`Failed to suspend provider: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    try {
      setLoading(true);
      await providersApi.unsuspendProvider(id);
      await fetchProvider();
      showSuccess('Provider has been unsuspended.');
    } catch (err) {
      showError(`Failed to unsuspend provider: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getAverageRating = (reviews) => {
    if (!reviews || reviews.length === 0) return 0;
    return reviews.reduce((total, review) => total + review.rating, 0) / reviews.length;
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !provider) {
    return (
      <Container sx={{ mt: 4 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/providers')}>Providers</Button>
        </Stack>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!provider) {
    return (
      <Container sx={{ mt: 4 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/providers')}>Providers</Button>
        </Stack>
        <Alert severity="warning">Provider not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/providers')}>Providers</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {provider.images && provider.images.length > 0 ? (
              <Card>
                <CardMedia
                  component="img"
                  height="250"
                  image={providerImageUrl(provider.images[0], 'large')}
                  alt={provider.title}
                  sx={{ objectFit: 'cover' }}
                />
              </Card>
            ) : (
              <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200', borderRadius: 1 }}>
                <StoreIcon sx={{ fontSize: 80, color: 'grey.400' }} />
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h4" component="h1">
                {provider.title}
              </Typography>
              {/* The SAME chip component the list renders, so the two pages speak one
                  vocabulary. Origin sits alongside it. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={provider.intakeSource || '-'} size="small" variant="outlined" />
                {/* providerType is displayed here specifically because the PRO switch below
                    rejects a grant on a non-SERVICE provider. Without it an admin gets a bare
                    400 and nowhere in the UI to discover the provider is a STORE. */}
                <Chip
                  label={provider.providerType || 'SERVICE'}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <ProviderStatusChips provider={provider} />
              </Box>
            </Box>

            {/* Every admin decision in one place. Approve/Decline/Suspend used to sit loose in
                the header and the visibility switch was buried inside the edit panel. */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Admin actions</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" color="success" variant="contained" startIcon={<CheckCircleIcon />} onClick={handleApproveProvider}>
                  Approve
                </Button>
                <Button size="small" color="error" variant="contained" startIcon={<CancelIcon />} onClick={handleDeclineProvider}>
                  Decline
                </Button>
                {provider.adminSuspended ? (
                  <Button size="small" color="warning" variant="outlined" startIcon={<CheckCircleIcon />} onClick={handleUnsuspend}>
                    Unsuspend
                  </Button>
                ) : (
                  <Button size="small" color="error" variant="outlined" startIcon={<BlockIcon />} onClick={handleSuspend}>
                    Suspend
                  </Button>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={provider.visible !== false}
                      disabled={busyVisibility || provider.adminSuspended}
                      onChange={handleToggleVisible}
                    />
                  }
                  label={provider.visible !== false ? 'Visible' : 'Hidden'}
                />
              </Box>
            </Paper>

            <ProviderSubscriptionCard provider={provider} onChanged={fetchProvider} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Rating value={getAverageRating(provider.reviews)} precision={0.5} readOnly />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({provider.reviews?.length || 0} reviews)
              </Typography>
            </Box>

            <Typography variant="body1" paragraph>
              {provider.about || 'No description available.'}
            </Typography>

            {/* Durable record — survives the 90-day application purge, so this view is
                the audit path for provider-terms acceptance. */}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>Legal acceptance</Typography>
            <Typography variant="body2" color="text.secondary">
              {provider.providerTermsVersion
                ? `Provider terms ${provider.providerTermsVersion} (${provider.providerTermsLocale || '?'})`
                  + (provider.providerTermsAcceptedAt
                    ? ` — accepted ${new Date(provider.providerTermsAcceptedAt).toLocaleString()}`
                    : '')
                : 'No provider-terms acceptance recorded'}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {provider.user && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PersonIcon />
                      </ListItemIcon>
                      <Typography variant="body2">
                        <Link to={`/users/${provider.user.id}`} style={{ color: 'inherit', fontWeight: 600 }}>
                          {provider.user.name || 'View User'}
                        </Link>
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PhoneIcon />
                      </ListItemIcon>
                      <Typography variant="body2">
                        {provider.user.phone || 'No phone number'}
                      </Typography>
                    </Box>
                  </Grid>
                  {(provider.user.city || provider.user.country) && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <LocationOn />
                        </ListItemIcon>
                        <Typography variant="body2">
                          {[provider.user.city, provider.user.country].filter(Boolean).join(', ')}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {provider.user.address && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <LocationOn />
                        </ListItemIcon>
                        <Typography variant="body2">{provider.user.address}</Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <EmailIcon />
                      </ListItemIcon>
                      <Typography variant="body2">
                        {provider.user.email || 'No email'}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}

              {provider.instagramUser && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <InstagramIcon />
                    </ListItemIcon>
                    <Typography variant="body2">@{provider.instagramUser}</Typography>
                  </Box>
                </Grid>
              )}

              {provider.documentUrl && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <Typography variant="body2">
                      <a href={provider.documentUrl} target="_blank" rel="noopener noreferrer">View Document</a>
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CalendarIcon />
                  </ListItemIcon>
                  <Typography variant="body2">
                    Created: {provider.createdAt ? new Date(provider.createdAt).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <UpdateIcon />
                  </ListItemIcon>
                  <Typography variant="body2">
                    Updated: {provider.updatedAt ? new Date(provider.updatedAt).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <VisibilityIcon />
                  </ListItemIcon>
                  <Typography variant="body2">
                    Visible: {provider.visible !== null ? provider.visible.toString() : 'N/A'}
                    {provider.adminSuspended && ' (admin suspended)'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="overview" label="Overview" />
        <Tab value="services" label="Services" />
        <Tab value="orders" label="Orders" />
        <Tab value="reviews" label="Reviews" />
        <Tab value="media" label="Media" />
      </Tabs>

      {/* onUpdated={setProvider} on EVERY instance. Without it a save no longer refreshes the
          parent, so the status chips and the Origin chip go stale after each edit. */}
      {tab === 'overview' && (
        <>
          {/* STORE providers only: the endpoint 400s for any other type. */}
          {provider.providerType === 'STORE' && <StoreCommissionSection providerId={provider.id} />}
          <SeededProviderPanel provider={provider} showSuccess={showSuccess} showError={showError} />
          <ProviderAdminEditPanel
            section="details"
            provider={provider}
            onUpdated={setProvider}
            showSuccess={showSuccess}
            showError={showError}
          />
        </>
      )}

      {/* The EDITABLE services block only. The read-only Services section that used to sit
          here rendered the same data a second time. */}
      {tab === 'services' && (
        <ProviderAdminEditPanel
          section="services"
          provider={provider}
          onUpdated={setProvider}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      {tab === 'orders' && (<>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom>Orders</Typography>
          {ordersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : orders.length > 0 ? (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow
                        key={order.id}
                        hover
                        onClick={() => navigate(`/orders/${order.id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{order.id}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            size="small"
                            color={order.status === 'PAID' || order.status === 'COMPLETED' ? 'success' :
                                   order.status?.startsWith('CANCELLED') ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{order.dogServiceType ? order.dogServiceType.replace(/_/g, ' ') : order.orderType}</TableCell>
                        <TableCell>{order.user?.name || '-'}</TableCell>
                        <TableCell>
                          {order.price ? `${order.price} ${order.currencyCode || ''}` : (order.calculatedTotal ? `${order.calculatedTotal} ${order.currencyCode || ''}` : '-')}
                        </TableCell>
                        <TableCell>{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={ordersTotal}
                rowsPerPage={ordersRowsPerPage}
                page={ordersPage}
                onPageChange={(_, newPage) => setOrdersPage(newPage)}
                onRowsPerPageChange={(e) => { setOrdersRowsPerPage(parseInt(e.target.value, 10)); setOrdersPage(0); }}
              />
            </>
          ) : (
            <Typography variant="body1" color="text.secondary">No orders found.</Typography>
          )}
        </Paper>
      </>)}

      {tab === 'reviews' && <AdminReviewsTab providerId={provider.id} />}

      {/* The EDITABLE image block, upload and delete included. It renders the FULL set --
          the deleted Gallery showed images.slice(1) because the header card carried
          images[0], so neither place was complete on its own. */}
      {tab === 'media' && (
        <ProviderAdminEditPanel
          section="images"
          provider={provider}
          onUpdated={setProvider}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}
    </Container>
  );
};

export default ProviderDetails;
